import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Sparkles, ArrowLeft, User, Briefcase, Mail, Phone, Globe, Linkedin, Twitter, Github, Instagram, Upload, AlertCircle, Trash2, MessageCircle, Images, Youtube, FileText, Crown, Lock } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../contexts/AuthContext';
import { useSubscriptionLimits } from '../hooks/useSubscriptionLimits';
import { cardAPI } from '../services/api';
import { compressImage, getBase64Size, isValidImageType } from '../utils/imageCompression';
import { validateAllUrls, getValidationError } from '../utils/urlValidation';
import ImageCropDialog from '../components/ImageCropDialog';
import RichTextEditor from '../components/RichTextEditor';
import GalleryUpload from '../components/GalleryUpload';
import YouTubeLinks from '../components/YouTubeLinks';
import { uploadImage as firebaseUploadImage } from '../firebase/config';
import { COUNTRY_CODES, parsePhoneNumber, formatPhoneNumber, DEFAULT_COUNTRY_CODE } from '../utils/countryCodes';
import CountryCodeSelect from '../components/CountryCodeSelect';

const ADMIN_EMAILS = ['sagarshiva111555@gmail.com', 'allgrowtechonologies@gmail.com'];

const EditCard = () => {
  const navigate = useNavigate();
  const { username } = useParams();
  const { toast } = useToast();
  const { user } = useAuth();
  const { isSubscribed } = useSubscriptionLimits();
  const [loading, setLoading] = useState(true);
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    title: '',
    company: '',
    email: '',
    phone: '',
    phoneCountryCode: DEFAULT_COUNTRY_CODE,
    websiteName: '',
    websiteTLD: '.com',
    location: '',
    locationAddress: '',
    bio: '',
    richBio: '',
    profileImage: '',
    companyLogo: '',
    qrColor: '#4F46E5', // Default indigo color
    // backgroundImage: '', // Removed - not needed
    linkedin: '',
    twitter: '',
    github: '',
    instagram: '',
    youtube: '',
    facebook: '',
    whatsapp: '',
    whatsappCountryCode: DEFAULT_COUNTRY_CODE,
    galleryImages: [],
    youtubeVideos: []
  });
  const [imagePreview, setImagePreview] = useState('');
  const [logoPreview, setLogoPreview] = useState('');
  // const [backgroundPreview, setBackgroundPreview] = useState(''); // Removed - not needed
  const [validationErrors, setValidationErrors] = useState({});
  const [originalName, setOriginalName] = useState(''); // Store original name
  const [originalGalleryImages, setOriginalGalleryImages] = useState(null); // Track original gallery images
  
  // Name availability check states
  const [nameCheckLoading, setNameCheckLoading] = useState(false);
  const [nameAvailable, setNameAvailable] = useState(true);
  const [nameErrorMessage, setNameErrorMessage] = useState('');
  
  // Crop dialog states
  const [cropDialogOpen, setCropDialogOpen] = useState(false);
  const [imageToCrop, setImageToCrop] = useState(null);
  const [cropType, setCropType] = useState('profile'); // 'profile' or 'logo'

  // Check if current user is admin
  const isAdmin = user?.email && ADMIN_EMAILS.includes(user.email.toLowerCase());

  useEffect(() => {
    const loadCard = async () => {
      try {
        const card = await cardAPI.getByUsername(username);
        
        // Check if user owns this card OR is admin
        if (card.userId !== user?.uid && !isAdmin) {
          toast({
            title: "Access Denied",
            description: "You can only edit your own cards.",
            variant: "destructive"
          });
          navigate('/my-cards');
          return;
        }

        // Extract address from Google Maps URL if present
        let locationAddress = '';
        if (card.location) {
          try {
            const url = new URL(card.location);
            const query = url.searchParams.get('query');
            if (query) {
              locationAddress = decodeURIComponent(query);
            }
          } catch (e) {
            // If URL parsing fails, leave address empty
          }
        }

        // Parse phone number to extract country code
        const parsedPhone = parsePhoneNumber(card.phone || '');

        setFormData({
          name: card.name || '',
          title: card.title || '',
          company: card.company || '',
          email: card.email || '',
          phone: parsedPhone.number,
          phoneCountryCode: parsedPhone.countryCode,
          websiteName: card.websiteName || '',
          websiteTLD: card.websiteTLD || '.com',
          location: card.location || '',
          locationAddress: locationAddress,
          bio: card.bio || '',
          // Auto-convert plain bio to richBio if richBio is empty but bio exists
          richBio: card.richBio || (card.bio ? card.bio
            .split('\n')
            .filter(line => line.trim())
            .map(line => `<p>${line}</p>`)
            .join('') : ''),
          profileImage: card.profileImage || '',
          companyLogo: card.companyLogo || '',
          qrColor: card.qrColor || '#4F46E5', // Default indigo color
          // backgroundImage: card.backgroundImage || '', // Removed - not needed
          linkedin: card.linkedin || '',
          twitter: card.twitter || '',
          github: card.github || '',
          instagram: card.instagram || '',
          youtube: card.youtube || '',
          facebook: card.facebook || '',
          whatsapp: parsePhoneNumber(card.whatsapp || '').number,
          whatsappCountryCode: parsePhoneNumber(card.whatsapp || '').countryCode,
          galleryImages: card.galleryImages || [],
          youtubeVideos: card.youtubeVideos || []
        });
        
        // Store original name for comparison
        setOriginalName(card.name || '');
        
        // Store original gallery images to prevent accidental clearing
        setOriginalGalleryImages(card.galleryImages || null);
        
        if (card.profileImage) {
          setImagePreview(card.profileImage);
        }
        
        if (card.companyLogo) {
          setLogoPreview(card.companyLogo);
        }
        
        // Background image preview removed - not needed
        
        setLoading(false);
      } catch (error) {
        console.error('Error loading card:', error);
        toast({
          title: "Error",
          description: "Failed to load card data.",
          variant: "destructive"
        });
        navigate('/my-cards');
      }
    };

    if (user) {
      loadCard();
    } else {
      navigate('/');
    }
  }, [username, user, navigate, toast]);

  // Debounced name availability check (only if name changed from original)
  useEffect(() => {
    // If name hasn't changed from original, it's always available
    if (formData.name === originalName) {
      setNameAvailable(true);
      setNameErrorMessage('');
      return;
    }

    if (!formData.name || formData.name.trim().length < 2) {
      setNameAvailable(true);
      setNameErrorMessage('');
      return;
    }

    const checkNameAvailability = async () => {
      setNameCheckLoading(true);
      try {
        const result = await cardAPI.checkNameAvailability(formData.name.trim(), username);
        setNameAvailable(result.available);
        setNameErrorMessage(result.available ? '' : result.message);
      } catch (error) {
        console.error('Error checking name availability:', error);
        // If check fails, allow submission (backend will validate)
        setNameAvailable(true);
        setNameErrorMessage('');
      } finally {
        setNameCheckLoading(false);
      }
    };

    const timeoutId = setTimeout(checkNameAvailability, 500);
    return () => clearTimeout(timeoutId);
  }, [formData.name, originalName, username]);

  // Parse website URL to extract domain and TLD
  const parseWebsiteUrl = (url) => {
    if (!url) return { websiteName: '', websiteTLD: '.com' };
    
    try {
      let cleanUrl = url.trim();
      
      // Remove protocol (http://, https://)
      cleanUrl = cleanUrl.replace(/^https?:\/\//i, '');
      
      // Remove www.
      cleanUrl = cleanUrl.replace(/^www\./i, '');
      
      // Remove trailing slashes and paths
      cleanUrl = cleanUrl.split('/')[0];
      
      // Remove query parameters
      cleanUrl = cleanUrl.split('?')[0];
      
      // Split by dots to get domain parts
      const parts = cleanUrl.split('.');
      
      if (parts.length < 2) {
        // No TLD found, return as is
        return { websiteName: cleanUrl, websiteTLD: '.com' };
      }
      
      // Handle multi-part TLDs like .co.uk, .co.in
      let tld;
      let domain;
      
      if (parts.length >= 3 && ['co', 'com', 'ac', 'gov', 'org', 'net'].includes(parts[parts.length - 2])) {
        // Multi-part TLD (e.g., .co.uk, .co.in)
        tld = `.${parts[parts.length - 2]}.${parts[parts.length - 1]}`;
        domain = parts.slice(0, -2).join('.');
      } else {
        // Single-part TLD (e.g., .com, .in, .org)
        tld = `.${parts[parts.length - 1]}`;
        domain = parts.slice(0, -1).join('.');
      }
      
      return { websiteName: domain, websiteTLD: tld };
    } catch (error) {
      console.error('Error parsing website URL:', error);
      return { websiteName: url, websiteTLD: '.com' };
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'websiteName') {
      // Parse website URL to extract domain and TLD
      const parsed = parseWebsiteUrl(value);
      setFormData(prev => ({ 
        ...prev, 
        websiteName: parsed.websiteName,
        websiteTLD: parsed.websiteTLD
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!isValidImageType(file)) {
        toast({
          title: "Invalid file type",
          description: "Please upload a valid image file (JPEG, PNG, WebP, or GIF).",
          variant: "destructive"
        });
        return;
      }

      // Check file size (max 15MB)
      if (file.size > 15 * 1024 * 1024) {
        toast({
          title: "File too large",
          description: "Please upload an image smaller than 15MB.",
          variant: "destructive"
        });
        return;
      }

      // Read file and open crop dialog
      const reader = new FileReader();
      reader.onload = () => {
        setImageToCrop(reader.result);
        setCropType('profile');
        setCropDialogOpen(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleLogoUpload = async (e) => {
    // Check for subscription (Pro/Business) first - only free users are restricted
    if (!isSubscribed) {
      setShowPremiumModal(true);
      e.target.value = '';
      return;
    }

    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!isValidImageType(file)) {
        toast({
          title: "Invalid file type",
          description: "Please upload a valid image file (JPEG, PNG, WebP, or GIF).",
          variant: "destructive"
        });
        return;
      }

      // Check file size (max 15MB)
      if (file.size > 15 * 1024 * 1024) {
        toast({
          title: "File too large",
          description: "Please upload an image smaller than 15MB.",
          variant: "destructive"
        });
        return;
      }

      // Read file and open crop dialog
      const reader = new FileReader();
      reader.onload = () => {
        setImageToCrop(reader.result);
        setCropType('logo');
        setCropDialogOpen(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCropComplete = async (croppedImageBlob) => {
    try {
      toast({
        title: "Uploading image...",
        description: "Please wait while we upload your high-quality image."
      });

      // Convert blob to file - keep original quality
      const file = new File([croppedImageBlob], 'cropped-image.jpg', { type: 'image/jpeg' });
      const originalSize = Math.round(file.size / 1024);
      
      // Upload directly to Firebase Storage without compression for high quality
      let imageUrl;
      try {
        const storagePath = `${cropType === 'logo' ? 'company-logos' : 'profile-images'}/${user.uid}-${Date.now()}.jpg`;
        imageUrl = await firebaseUploadImage(file, storagePath);
      } catch (fbErr) {
        console.error('Firebase upload failed:', fbErr);
        toast({
          title: "Upload failed",
          description: "Failed to upload image to storage. Please try again.",
          variant: "destructive"
        });
        return;
      }

      if (cropType === 'profile') {
        setFormData(prev => ({ ...prev, profileImage: imageUrl }));
        setImagePreview(imageUrl);
      } else if (cropType === 'logo') {
        setFormData(prev => ({ ...prev, companyLogo: imageUrl }));
        setLogoPreview(imageUrl);
      }

      const typeNames = { profile: 'Photo', logo: 'Logo' };
      toast({
        title: `${typeNames[cropType]} uploaded successfully`,
        description: `High-quality image uploaded (${originalSize}KB)`
      });
    } catch (error) {
      console.error('Image upload error:', error);
      toast({
        title: "Upload failed",
        description: "Failed to process image. Please try another file.",
        variant: "destructive"
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.title || !formData.company) {
      toast({
        title: "Missing Information",
        description: "Please fill in name, email, job title, and company.",
        variant: "destructive"
      });
      return;
    }

    // WhatsApp validation - now we have country code separately, just check number length
    if (formData.whatsapp && formData.whatsapp.trim()) {
      const digitsOnly = formData.whatsapp.replace(/[^0-9]/g, '');
      if (digitsOnly.length < 6) {
        toast({
          title: "Invalid WhatsApp Number",
          description: "Please enter a valid WhatsApp number",
          variant: "destructive"
        });
        return;
      }
    }

    // Check name availability (only if name changed)
    if (formData.name !== originalName && !nameAvailable) {
      toast({
        title: "Card Name Not Available",
        description: nameErrorMessage || "Please choose a different card name.",
        variant: "destructive"
      });
      return;
    }

    try{
      // Prepare data for update - only include galleryImages if it was modified
      const updateData = { ...formData };
      
      // Format phone number with country code
      if (formData.phone) {
        updateData.phone = formatPhoneNumber(formData.phoneCountryCode, formData.phone);
      }
      
      // Format WhatsApp number with country code
      if (formData.whatsapp) {
        updateData.whatsapp = formatPhoneNumber(formData.whatsappCountryCode, formData.whatsapp);
      }
      
      // Remove country code fields as they're not stored separately
      delete updateData.phoneCountryCode;
      delete updateData.whatsappCountryCode;
      
      // Remove locationAddress as it's only used in UI - backend stores location (the URL)
      delete updateData.locationAddress;
      
      // If galleryImages is empty and original was null/undefined, don't send it
      // This prevents accidentally clearing images that exist in DB but weren't loaded
      const galleryWasModified = JSON.stringify(formData.galleryImages) !== JSON.stringify(originalGalleryImages || []);
      if (!galleryWasModified && formData.galleryImages.length === 0 && originalGalleryImages === null) {
        delete updateData.galleryImages;
      }
      
      await cardAPI.update(username, updateData);
      
      // Invalidate cache to ensure card updates immediately
      try {
        const cacheManager = (await import('../utils/cache')).default;
        cacheManager.clearAll();
        
        // Also invalidate userCard cache
        const { invalidateUserCardCache } = await import('../hooks/useUserCard');
        invalidateUserCardCache();
      } catch (error) {
        console.error('Error clearing cache:', error);
      }
      
      toast({
        title: "Card Updated!",
        description: "Your changes have been saved."
      });

      setTimeout(() => {
        navigate(`/bio/${username}`);
      }, 1000);
    } catch (error) {
      console.error('Error updating card:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to update card. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this card? This action cannot be undone.')) {
      return;
    }

    try {
      await cardAPI.delete(username);
      
      // Invalidate cache to ensure card deletion reflects immediately
      try {
        const cacheManager = (await import('../utils/cache')).default;
        cacheManager.clearAll();
      } catch (error) {
        console.error('Error clearing cache:', error);
      }
      
      toast({
        title: "Card Deleted",
        description: "Your card has been successfully deleted."
      });
      
      // Redirect to home page after deletion
      setTimeout(() => {
        navigate('/');
      }, 1000);
    } catch (error) {
      console.error('Error deleting card:', error);
      toast({
        title: "Error",
        description: "Failed to delete card. Please try again.",
        variant: "destructive"
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-teal-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading card...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-teal-50 py-6 sm:py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <div className="mb-6 sm:mb-8">
          <Link to={`/bio/${username}`} className="inline-flex items-center text-amber-600 hover:text-amber-700 mb-3 sm:mb-4 transition-colors text-sm sm:text-base">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Card
          </Link>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-2">
            Edit Your <span className="bg-gradient-to-r from-amber-600 to-yellow-500 bg-clip-text text-transparent">Digital Card</span>
          </h1>
          <p className="text-sm sm:text-base text-gray-600">Update your professional digital business card</p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4 sm:space-y-6">
            {/* Basic Information */}
            <Card className="border-2 border-gray-100">
              <CardHeader className="p-4 sm:p-6">
                <CardTitle className="flex items-center text-lg sm:text-xl lg:text-2xl">
                  <User className="w-5 h-5 sm:w-6 sm:h-6 mr-2 text-amber-600" />
                  Basic Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 p-4 sm:p-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <Label htmlFor="name">Full Name *</Label>
                    <div className="relative">
                      <Input
                        id="name"
                        name="name"
                        placeholder="John Doe"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className={`mt-1 ${!nameAvailable && formData.name !== originalName ? 'border-red-500 focus:border-red-500' : ''}`}
                      />
                      {nameCheckLoading && formData.name !== originalName && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2 mt-0.5">
                          <div className="w-4 h-4 border-2 border-amber-600 border-t-transparent rounded-full animate-spin"></div>
                        </div>
                      )}
                    </div>
                    {!nameAvailable && formData.name !== originalName && nameErrorMessage && (
                      <p className="text-xs text-red-500 mt-1 flex items-start gap-1">
                        <AlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                        <span>{nameErrorMessage}</span>
                      </p>
                    )}
                    {nameAvailable && formData.name !== originalName && formData.name.trim().length >= 2 && !nameCheckLoading && (
                      <p className="text-xs text-green-600 mt-1">
                        ✓ This name is available
                      </p>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="title">Job Title *</Label>
                    <Input
                      id="title"
                      name="title"
                      placeholder="Senior Product Designer"
                      value={formData.title}
                      onChange={handleChange}
                      className="mt-1"
                      required
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="company">Company *</Label>
                  <Input
                    id="company"
                    name="company"
                    placeholder="TechCorp Inc."
                    value={formData.company}
                    onChange={handleChange}
                    className="mt-1"
                    required
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label htmlFor="richBio" className="flex items-center">
                      <FileText className="w-4 h-4 mr-1" />
                      Bio
                    </Label>
                    {formData.bio && formData.richBio && !formData.richBio.includes('<strong>') && !formData.richBio.includes('<em>') && (
                      <span className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full">
                        Auto-loaded from existing bio
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mb-2">Rich text with formatting - headings, lists, links, colors & alignment</p>
                  <RichTextEditor
                    value={formData.richBio}
                    onChange={(value) => setFormData(prev => ({ ...prev, richBio: value }))}
                    placeholder="Write your bio with formatting..."
                  />
                </div>

                <div>
                  <Label htmlFor="profileImage">Profile Photo</Label>
                  <div className="mt-1 flex flex-col sm:flex-row gap-4 items-start">
                    <div className="flex-1 w-full">
                      <div className="flex items-center gap-2">
                        <Input
                          id="profileImageFile"
                          type="file"
                          accept="image/*"
                          onChange={handleImageUpload}
                          className="hidden"
                        />
                        <label 
                          htmlFor="profileImageFile"
                          className="flex-1 cursor-pointer"
                        >
                          <div className="flex items-center justify-center gap-2 px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg hover:border-amber-400 transition-colors">
                            <Upload className="w-5 h-5 text-gray-400" />
                            <span className="text-sm text-gray-600">Upload Photo</span>
                          </div>
                        </label>
                      </div>
                    </div>
                    {(imagePreview || formData.profileImage) && (
                      <div className="flex-shrink-0">
                        <img 
                          src={imagePreview || formData.profileImage} 
                          alt="Preview"
                          className="w-20 h-20 rounded-full object-cover border-2 border-amber-200"
                        />
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <Label htmlFor="companyLogo">Company Logo (Optional)</Label>
                  <p className="text-xs text-gray-500 mb-2">This will appear inside your QR code</p>
                  
                  {isSubscribed ? (
                    <div className="mt-1 flex flex-col sm:flex-row gap-4 items-start">
                      <div className="flex-1 w-full">
                        <div className="flex items-center gap-2">
                          <Input
                            id="companyLogoFile"
                            type="file"
                            accept="image/*"
                            onChange={handleLogoUpload}
                            className="hidden"
                          />
                          <label 
                            htmlFor="companyLogoFile"
                            className="flex-1 cursor-pointer"
                          >
                            <div className="flex items-center justify-center gap-2 px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg hover:border-teal-400 transition-colors">
                              <Upload className="w-5 h-5 text-gray-400" />
                              <span className="text-sm text-gray-600">Upload Logo</span>
                            </div>
                          </label>
                        </div>
                      </div>
                      {(logoPreview || formData.companyLogo) && (
                        <div className="flex-shrink-0">
                          <img 
                            src={logoPreview || formData.companyLogo} 
                            alt="Logo Preview"
                            className="w-20 h-20 rounded-lg object-contain border-2 border-teal-200 bg-white p-2"
                          />
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="p-6 bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl border-2 border-dashed border-amber-300">
                      <div className="text-center">
                        <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-amber-100 flex items-center justify-center">
                          <Lock className="w-7 h-7 text-amber-600" />
                        </div>
                        <h4 className="font-semibold text-gray-900 mb-2">Pro/Business Feature</h4>
                        <p className="text-sm text-gray-600 mb-4">Upload your company logo and get it embedded in your QR code</p>
                        <Button 
                          type="button"
                          className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white"
                          onClick={() => navigate('/pricing')}
                        >
                          <Crown className="w-4 h-4 mr-2" />
                          Upgrade Now
                        </Button>
                      </div>
                    </div>
                  )}
                </div>

                {/* QR Code Color */}
                <div>
                  <Label htmlFor="qrColor">QR Code Color</Label>
                  <p className="text-xs text-gray-500 mb-2">Choose the color for your QR code</p>
                  <div className="flex items-center gap-3">
                    <Input
                      id="qrColor"
                      type="color"
                      value={formData.qrColor}
                      onChange={(e) => setFormData({ ...formData, qrColor: e.target.value })}
                      className="w-20 h-10 cursor-pointer"
                    />
                    <Input
                      type="text"
                      value={formData.qrColor}
                      onChange={(e) => setFormData({ ...formData, qrColor: e.target.value })}
                      placeholder="#4F46E5"
                      className="flex-1"
                      maxLength={7}
                    />
                    <div 
                      className="w-10 h-10 rounded-lg border-2 border-gray-300"
                      style={{ backgroundColor: formData.qrColor }}
                    />
                  </div>
                </div>

                {/* Background Image Upload - Removed as per requirement */}
              </CardContent>
            </Card>

            {/* Contact Information */}
            <Card className="border-2 border-gray-100">
              <CardHeader className="p-4 sm:p-6">
                <CardTitle className="flex items-center text-lg sm:text-xl lg:text-2xl">
                  <Briefcase className="w-5 h-5 sm:w-6 sm:h-6 mr-2 text-teal-600" />
                  Contact Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 p-4 sm:p-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <Label htmlFor="email" className="flex items-center">
                      <Mail className="w-4 h-4 mr-1" />
                      Email *
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="john@example.com"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="mt-1"
                    />
                    <p className="text-xs text-gray-500 mt-1">This email will be displayed on your business card</p>
                  </div>
                  <div>
                    <Label htmlFor="phone" className="flex items-center">
                      <Phone className="w-4 h-4 mr-1" />
                      Phone
                    </Label>
                    <div className="flex gap-2 mt-1">
                      <CountryCodeSelect
                        value={formData.phoneCountryCode}
                        onChange={(e) => setFormData(prev => ({ ...prev, phoneCountryCode: e.target.value }))}
                        className="w-36"
                      />
                      <Input
                        id="phone"
                        name="phone"
                        placeholder="9876543210"
                        value={formData.phone}
                        onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value.replace(/[^\d\s\-()]/g, '') }))}
                        className="flex-1"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <Label htmlFor="websiteName" className="flex items-center">
                    <Globe className="w-4 h-4 mr-1" />
                    Website
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      id="websiteName"
                      name="websiteName"
                      placeholder="example"
                      value={formData.websiteName}
                      onChange={handleChange}
                      className="mt-1 flex-1"
                    />
                    <select
                      name="websiteTLD"
                      value={formData.websiteTLD}
                      onChange={handleChange}
                      className="mt-1 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                    >
                      <option value=".com">.com</option>
                      <option value=".in">.in</option>
                      <option value=".org">.org</option>
                      <option value=".net">.net</option>
                      <option value=".co">.co</option>
                      <option value=".io">.io</option>
                      <option value=".ai">.ai</option>
                      <option value=".dev">.dev</option>
                    </select>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Enter domain name without www or https</p>
                </div>

                <div>
                  <Label htmlFor="locationAddress" className="flex items-center">
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    Location Address
                  </Label>
                  <Input
                    id="locationAddress"
                    name="locationAddress"
                    placeholder="Enter address or paste Google Maps URL"
                    value={formData.locationAddress || ''}
                    onChange={(e) => {
                      const input = e.target.value;
                      
                      // Check if user pasted a Google Maps URL
                      if (input.includes('google.com/maps') || input.includes('maps.google.com') || input.includes('goo.gl/maps')) {
                        try {
                          const url = new URL(input);
                          // Try to extract address from URL
                          let extractedAddress = '';
                          
                          // Check for query parameter
                          const query = url.searchParams.get('query') || url.searchParams.get('q');
                          if (query) {
                            extractedAddress = decodeURIComponent(query);
                          }
                          
                          // Check for place name in path (e.g., /maps/place/Address+Here/)
                          const placeMatch = input.match(/\/place\/([^\/]+)/);
                          if (!extractedAddress && placeMatch) {
                            extractedAddress = decodeURIComponent(placeMatch[1].replace(/\+/g, ' '));
                          }
                          
                          // If we found an address, use it; otherwise use the URL directly
                          if (extractedAddress) {
                            setFormData(prev => ({
                              ...prev,
                              locationAddress: extractedAddress,
                              location: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(extractedAddress)}`
                            }));
                            toast({
                              title: "Address Extracted",
                              description: `Found: ${extractedAddress}`,
                            });
                            return;
                          } else {
                            // Keep the original URL if we couldn't extract address
                            setFormData(prev => ({
                              ...prev,
                              locationAddress: input,
                              location: input
                            }));
                            return;
                          }
                        } catch (e) {
                          // Not a valid URL, treat as address
                        }
                      }
                      
                      // Regular address input
                      setFormData(prev => ({
                        ...prev,
                        locationAddress: input,
                        location: input ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(input)}` : ''
                      }));
                    }}
                    className="mt-1"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Enter your address or paste a Google Maps URL - it will automatically create a link
                    {formData.location && (
                      <a 
                        href={formData.location} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="ml-2 text-blue-600 hover:text-blue-700 underline"
                      >
                        Preview on Google Maps
                      </a>
                    )}
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Social Links */}
            <Card className="border-2 border-gray-100">
              <CardHeader className="p-4 sm:p-6">
                <CardTitle className="text-lg sm:text-xl lg:text-2xl">Social Links</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 p-4 sm:p-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <Label htmlFor="linkedin" className="flex items-center">
                      <Linkedin className="w-4 h-4 mr-1" />
                      LinkedIn
                    </Label>
                    <Input
                      id="linkedin"
                      name="linkedin"
                      placeholder="username or company/pipercard"
                      value={formData.linkedin}
                      onChange={handleChange}
                      className="mt-1"
                    />
                    <p className="text-xs text-gray-500 mt-1">Profile: username | Company: company/name</p>
                  </div>
                  <div>
                    <Label htmlFor="twitter" className="flex items-center">
                      <Twitter className="w-4 h-4 mr-1" />
                      Twitter
                    </Label>
                    <Input
                      id="twitter"
                      name="twitter"
                      placeholder="johndoe"
                      value={formData.twitter}
                      onChange={handleChange}
                      className="mt-1"
                    />
                    <p className="text-xs text-gray-500 mt-1">Handle without @</p>
                  </div>
                  <div>
                    <Label htmlFor="github" className="flex items-center">
                      <Github className="w-4 h-4 mr-1" />
                      GitHub
                    </Label>
                    <Input
                      id="github"
                      name="github"
                      placeholder="johndoe"
                      value={formData.github}
                      onChange={handleChange}
                      className="mt-1"
                    />
                    <p className="text-xs text-gray-500 mt-1">Username only</p>
                  </div>
                  <div>
                    <Label htmlFor="instagram" className="flex items-center">
                      <Instagram className="w-4 h-4 mr-1" />
                      Instagram
                    </Label>
                    <Input
                      id="instagram"
                      name="instagram"
                      placeholder="johndoe"
                      value={formData.instagram}
                      onChange={handleChange}
                      className="mt-1"
                    />
                    <p className="text-xs text-gray-500 mt-1">Handle without @</p>
                  </div>
                  <div>
                    <Label htmlFor="youtube" className="flex items-center">
                      <Globe className="w-4 h-4 mr-1" />
                      YouTube
                    </Label>
                    <Input
                      id="youtube"
                      name="youtube"
                      placeholder="@johndoe or channel ID"
                      value={formData.youtube}
                      onChange={handleChange}
                      className="mt-1"
                    />
                    <p className="text-xs text-gray-500 mt-1">Channel ID or @handle</p>
                  </div>
                  <div>
                    <Label htmlFor="facebook" className="flex items-center">
                      <Globe className="w-4 h-4 mr-1" />
                      Facebook
                    </Label>
                    <Input
                      id="facebook"
                      name="facebook"
                      placeholder="johndoe"
                      value={formData.facebook}
                      onChange={handleChange}
                      className="mt-1"
                    />
                    <p className="text-xs text-gray-500 mt-1">Username only</p>
                  </div>
                  <div>
                    <Label htmlFor="whatsapp" className="flex items-center">
                      <MessageCircle className="w-4 h-4 mr-1" />
                      WhatsApp
                    </Label>
                    <div className="flex gap-2 mt-1">
                      <CountryCodeSelect
                        value={formData.whatsappCountryCode}
                        onChange={(e) => setFormData(prev => ({ ...prev, whatsappCountryCode: e.target.value }))}
                        className="w-36"
                      />
                      <Input
                        id="whatsapp"
                        name="whatsapp"
                        placeholder="9876543210"
                        value={formData.whatsapp}
                        onChange={(e) => setFormData(prev => ({ ...prev, whatsapp: e.target.value.replace(/[^\d\s\-()]/g, '') }))}
                        className="flex-1"
                      />
                    </div>
                    <p className="text-xs mt-1 text-gray-500">
                      Select country code and enter your WhatsApp number
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Product Gallery */}
            <Card className="border-2 border-gray-100">
              <CardHeader className="p-4 sm:p-6">
                <CardTitle className="flex items-center text-lg sm:text-xl lg:text-2xl">
                  <Images className="w-5 h-5 sm:w-6 sm:h-6 mr-2 text-purple-600" />
                  Product Gallery
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 p-4 sm:p-6">
                <p className="text-sm text-gray-600 mb-4">
                  Showcase your products or work with up to 4 images. Images are displayed in a masonry layout on your bio page.
                </p>
                <GalleryUpload
                  images={formData.galleryImages}
                  onChange={(images) => setFormData(prev => ({ ...prev, galleryImages: images }))}
                  maxImages={4}
                />
              </CardContent>
            </Card>

            {/* YouTube Videos */}
            <Card className="border-2 border-gray-100">
              <CardHeader className="p-4 sm:p-6">
                <CardTitle className="flex items-center text-lg sm:text-xl lg:text-2xl">
                  <Youtube className="w-5 h-5 sm:w-6 sm:h-6 mr-2 text-red-600" />
                  YouTube Videos
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 p-4 sm:p-6">
                <p className="text-sm text-gray-600 mb-4">
                  Add up to 4 YouTube videos to showcase your content. The first video displays as the main featured video.
                </p>
                <YouTubeLinks
                  videos={formData.youtubeVideos}
                  onChange={(videos) => setFormData(prev => ({ ...prev, youtubeVideos: videos }))}
                  maxVideos={3}
                />
              </CardContent>
            </Card>

            {/* Submit Button */}
            <div className="flex justify-center gap-4 pt-4 sm:pt-6 pb-4">
              <Link to={`/bio/${username}`}>
                <Button 
                  type="button"
                  variant="outline"
                  size="lg" 
                  className="px-8 sm:px-12 py-5 sm:py-6 text-base sm:text-lg"
                >
                  Cancel
                </Button>
              </Link>
              <Button 
                type="submit" 
                size="lg" 
                disabled={(!nameAvailable && formData.name !== originalName) || (nameCheckLoading && formData.name !== originalName)}
                className="bg-gradient-to-r from-amber-600 to-yellow-500 hover:from-amber-700 hover:to-yellow-600 text-white px-8 sm:px-12 py-5 sm:py-6 text-base sm:text-lg transition-all duration-300 transform hover:scale-105 hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
              >
                <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                {nameCheckLoading && formData.name !== originalName ? 'Checking name...' : 'Save Changes'}
              </Button>
            </div>

            {/* Delete Button - At the end */}
            <div className="flex justify-center pt-8 pb-4 border-t border-gray-200">
              <Button 
                type="button"
                onClick={handleDelete}
                variant="outline"
                size="lg" 
                className="border-2 border-red-500 text-red-600 hover:bg-red-50 hover:border-red-600 px-8 sm:px-12 py-5 sm:py-6 text-base sm:text-lg transition-all duration-300"
              >
                <Trash2 className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                Delete Card
              </Button>
            </div>
          </div>
        </form>

        {/* Image Crop Dialog */}
        <ImageCropDialog
          open={cropDialogOpen}
          onOpenChange={setCropDialogOpen}
          imageSrc={imageToCrop}
          onCropComplete={handleCropComplete}
          aspectRatio={1}
          shape={cropType === 'profile' ? 'round' : 'rect'}
        />

        {/* Premium Upgrade Modal for Logo Feature */}
        <Dialog open={showPremiumModal} onOpenChange={setShowPremiumModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-xl">
                <Crown className="w-6 h-6 text-amber-500" />
                Pro/Business Feature
              </DialogTitle>
            </DialogHeader>
            
            <div className="py-4">
              <div className="text-center mb-6">
                <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-amber-100 to-orange-100 flex items-center justify-center">
                  <Lock className="w-10 h-10 text-amber-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Custom Logo Upload
                </h3>
                <p className="text-gray-600 text-sm">
                  Upload your company logo and get it embedded in your QR code. Stand out with a professional branded experience.
                </p>
              </div>
              
              <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-4 mb-6 border border-amber-200">
                <div className="flex items-center gap-3 mb-3">
                  <img 
                    src="/logo.png" 
                    alt="Piper Card" 
                    className="w-8 h-8"
                  />
                  <div>
                    <p className="font-semibold text-gray-900">Upgrade to Pro or Business</p>
                    <p className="text-sm text-amber-700">Unlock custom logo and more</p>
                  </div>
                </div>
                <ul className="text-sm text-gray-700 space-y-1.5">
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-amber-500 rounded-full"></span>
                    Upload custom company logo
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-amber-500 rounded-full"></span>
                    Logo embedded in QR code
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-amber-500 rounded-full"></span>
                    Unlimited cards & scans
                  </li>
                </ul>
              </div>
              
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowPremiumModal(false)}
                >
                  Maybe Later
                </Button>
                <Button
                  className="flex-1 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white"
                  onClick={() => {
                    setShowPremiumModal(false);
                    navigate('/pricing');
                  }}
                >
                  <Crown className="w-4 h-4 mr-2" />
                  Upgrade Now
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default EditCard;
